import ImgContainer from "components/common/img";
import DropzoneComponent from "components/customs/dropzone";
import React, { useEffect, useState } from "react";
import upload from "assets/img/upload.svg";
import { Toast } from "utility/helpers";
import { uploadFile, downloadS3File } from "services/services.general";
import styled from "styled-components";
import FilePreview from "./FilePreview";

const UploadPreview = styled.div`
  margin-top: 1rem;
`;

const maxFileSize = 50 * 1024 * 1024;

const StyledImgContainer = styled(ImgContainer)`
  margin-right: 0.5rem;
  height: 24px;
`;

const StyledDropzoneComponent = styled(DropzoneComponent)`
  width: auto;
  height: 65px;
  border: 1px dashed #a3a3a3;
`;

const CustomDropzone = ({
  handleUploadedFiles,
  handleDeleteFile,
  uploadedFiles,
  label,
  acceptedFileTypes,
  acceptedFileTypeErrorMsg,
  isDisabled,
}) => {
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleUploadFiles = async () => {
    try {
      await Promise.all(
        files?.map(async (file) => {
          await uploadFile({
            file,
            fileName: file.name,
            onProgress: (progress) => {
              setUploadProgress((prevProgress) => ({
                ...prevProgress,
                [file.name]: progress,
              }));

              const newFile = {
                name: file.name,
                size: file.size,
                percent: progress, // Track progress
                location: null, // No location yet during upload
              };
              handleUploadedFiles(newFile);
            },
            callback: (data) => {
              if (data) {
                const updatedFile = {
                  name: file.name,
                  size: file.size,
                  percent: 100, // Set progress to 100% once done
                  location: data, // Set the uploaded file location
                };
                handleUploadedFiles(updatedFile);
              }
            },
          });
        })
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error uploading files", error);
    }
  };

  const handleOnDrop = (fileArray) => {
    const filteredFiles = fileArray.filter((file) => {
      if (file.size > maxFileSize) {
        // 50MB in bytes
        Toast({
          type: 4,
          content: `${file.name} exceeds the 50MB size limit.`,
        });

        return false;
      }

      return true;
    });

    if (acceptedFileTypes) {
      const acceptedFiles = filteredFiles.filter((file) =>
        acceptedFileTypes.includes(file.type)
      );

      setFiles(acceptedFiles);

      if (!acceptedFiles.length) {
        Toast({
          type: 4,
          content: acceptedFileTypeErrorMsg,
        });
      }
    }

    if (!acceptedFileTypes) {
      setFiles(filteredFiles);
    }
  };

  const downloadFromS3 = async (location) => {
    const parsedUrl = new URL(location);
    const rawFilePath = parsedUrl.pathname.slice(1);
    const fileName = parsedUrl.pathname.split("/").pop();
    const path = rawFilePath.split("/")[0];

    const decodedFileName = decodeURIComponent(fileName);

    const filePath = `${path}/${decodedFileName}`;

    try {
      // eslint-disable-next-line no-await-in-loop
      const data = await downloadS3File(filePath);

      const link = document.createElement("a");
      link.href = URL.createObjectURL(data);
      link.download = decodedFileName;
      link.click();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error downloading ${decodedFileName}:`, error);
    }
  };

  useEffect(() => {
    if (files.length) {
      handleUploadFiles();
    }
  }, [files]);

  return (
    <>
      <StyledDropzoneComponent
        handleOnDrop={handleOnDrop}
        label={label}
        isDisabled={isDisabled}
        uploadIcon={<StyledImgContainer src={upload} />}
      />

      <UploadPreview>
        {uploadedFiles?.length !== 0
          ? uploadedFiles?.map((file, idx) => (
              <FilePreview
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
                handleDeleteFile={handleDeleteFile}
                isDisabled={isDisabled}
                percent={uploadProgress[file.name] || file.percent}
                fileDownload={downloadFromS3}
                fileUrl={file.location}
                {...file}
              />
            ))
          : null}
      </UploadPreview>
    </>
  );
};

export default CustomDropzone;
